import React from 'react'
import { Box, Typography, Chip, IconButton, Tooltip, Paper } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useTranslation } from 'react-i18next'
import { Task } from '../../types'
import { getTaskTypeLabel } from './taskTypeLabel'

interface TaskKanbanBoardProps {
  tasks: Task[]
  getAssigneeLabel: (task: Task) => string
  getYardLabel: (task: Task) => string
  onEdit: (task: Task) => void
  onDelete: (task: Task) => void
  onMarkDone: (task: Task) => void
}

const COLUMNS: Array<{ key: Task['status']; color: string }> = [
  { key: 'open', color: '#1976d2' },
  { key: 'in_progress', color: '#ed6c02' },
  { key: 'done', color: '#2e7d32' },
]

function TaskKanbanBoard({ tasks, getAssigneeLabel, getYardLabel, onEdit, onDelete, onMarkDone }: TaskKanbanBoardProps): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={2}>
      {COLUMNS.map(col => {
        const columnTasks = tasks.filter(task => task.status === col.key)
        return (
          <Box key={col.key}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t(`tasks.status.${col.key}`)}
              </Typography>
              <Chip label={columnTasks.length} size="small" sx={{ height: 22, fontSize: 11 }} />
            </Box>
            <Box
              sx={{
                bgcolor: 'rgba(0,0,0,0.03)',
                borderRadius: 2,
                borderTop: `3px solid ${col.color}`,
                minHeight: 300,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {columnTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                  {t('tasks.noTasks')}
                </Typography>
              ) : (
                columnTasks.map(task => (
                  <Paper key={task.id} elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2" fontWeight={600}>{task.title}</Typography>
                      <Chip label={getTaskTypeLabel(t, task.type)} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    </Box>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ fontSize: 12 }}>
                        {task.description}
                      </Typography>
                    )}
                    <Box display="flex" gap={1} mt={0.5}>
                      <Typography variant="caption" color="text.secondary">{getYardLabel(task)}</Typography>
                      <Typography variant="caption" color="text.secondary">{getAssigneeLabel(task)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" mt={0.5}>
                      {col.key !== 'done' && (
                        <Tooltip title={t('tasks.markDone')}>
                          <IconButton size="small" color="success" onClick={() => onMarkDone(task)}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('tasks.edit')}>
                        <IconButton size="small" color="primary" onClick={() => onEdit(task)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('tasks.delete')}>
                        <IconButton size="small" color="error" onClick={() => onDelete(task)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default TaskKanbanBoard
